import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Play, RefreshCw, CheckCircle2, FlaskConical, AlertTriangle } from "lucide-react";
import { useState } from "react";
import { api } from "@/lib/api";
import { ErrorState } from "@/components/empty-state";

export const Route = createFileRoute("/admin/gaming-dragons")({
  component: AdminGamingDragons,
});

type Currency = "EUR" | "GBP" | "USD";

function AdminGamingDragons() {
  const qc = useQueryClient();
  const [limit, setLimit] = useState<string>("");
  const [currency, setCurrency] = useState<Currency>("EUR");

  const status = useQuery({
    queryKey: ["admin-gamingdragons-status"],
    queryFn: () => api.adminGamingDragonsStatus(),
    refetchInterval: 4000,
  });
  const errors = useQuery({
    queryKey: ["admin-gamingdragons-errors"],
    queryFn: () => api.adminImportErrors(),
    refetchInterval: 8000,
  });
  const run = useMutation({
    mutationFn: (opts: { dryRun?: boolean }) =>
      api.adminImportGamingDragons({
        dryRun: opts.dryRun,
        currency,
        limit: limit ? Number(limit) : undefined,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-gamingdragons-status"] }),
  });

  const data = status.data?.data;
  const jobs = data?.jobs ?? [];
  const isRunning = jobs[0]?.status === "running" || run.isPending;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold">Gaming Dragons import</h1>
          <p className="max-w-2xl text-sm text-muted-foreground">
            Imports the Gaming Dragons CSV feed. One endpoint per currency (EUR / GBP / USD).
            Only in-stock rows become active offers; out-of-stock rows are kept as inactive
            so price history stays stable when a SKU returns. Console products (Xbox /
            PlayStation / Switch) are skipped so they never map onto Steam catalog entries.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-md border border-border bg-surface p-0.5">
            {(["EUR", "GBP", "USD"] as Currency[]).map((c) => (
              <button
                key={c}
                onClick={() => setCurrency(c)}
                className={`rounded px-3 py-1.5 text-xs font-semibold ${
                  currency === c ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <input
            type="number"
            min={1}
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            placeholder="Limit (optional)"
            className="w-40 rounded-md border border-border bg-surface px-3 py-2 text-sm"
          />
          <button
            onClick={() => run.mutate({ dryRun: true })}
            disabled={isRunning}
            className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-3 py-2 text-sm font-semibold disabled:opacity-50"
          >
            <FlaskConical className="h-4 w-4" /> Dry run
          </button>
          <button
            onClick={() => run.mutate({})}
            disabled={isRunning}
            className="inline-flex items-center gap-1 rounded-md bg-primary-gradient px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
          >
            <Play className="h-4 w-4" /> {isRunning ? "Running…" : `Import ${currency}`}
          </button>
        </div>
      </header>

      {run.isError && <ErrorState message={(run.error as Error).message} />}

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className="font-display text-lg font-semibold">Feeds</h3>
          <div className="mt-2 space-y-1 text-xs">
            {(data?.currencies ?? []).map((c) => (
              <div key={c.currency} className="flex items-center justify-between gap-2">
                <span className="font-mono font-semibold">{c.currency}</span>
                <span className="truncate text-muted-foreground" title={c.feedUrl}>
                  {c.feedUrl.replace(/^https?:\/\//, "")}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className="font-display text-lg font-semibold">Affiliate</h3>
          {data?.affiliate.configured ? (
            <div className="mt-2 flex items-center gap-1 text-xs text-success">
              <CheckCircle2 className="h-3 w-3" />
              <span className="font-mono">?{data.affiliate.tagKey}=…</span>
            </div>
          ) : (
            <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
              <AlertTriangle className="h-3 w-3" />
              None configured — set{" "}
              <code className="font-mono">GAMINGDRAGONS_AFFILIATE_TAG</code> and{" "}
              <code className="font-mono">GAMINGDRAGONS_AFFILIATE_VALUE</code> to enable.
            </div>
          )}
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className="font-display text-lg font-semibold">Shop</h3>
          <div className="mt-1 text-sm">
            {data?.shop?.name ?? "Not created yet — will be created on first non-dry run"}
          </div>
          <div className="text-xs text-muted-foreground">{data?.offerCount ?? 0} active offers</div>
          <button
            onClick={() => status.refetch()}
            className="mt-3 inline-flex items-center gap-1 rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-semibold"
          >
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>
      </div>

      <div>
        <h2 className="mb-3 font-display text-lg font-semibold">Recent jobs</h2>
        {status.isError && <ErrorState message={(status.error as Error).message} />}
        {jobs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No Gaming Dragons imports have run yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-surface text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Started</th>
                  <th className="px-4 py-3">Finished</th>
                  <th className="px-4 py-3 text-right">Matched</th>
                  <th className="px-4 py-3 text-right">Unmatched</th>
                  <th className="px-4 py-3 text-right">Failed</th>
                  <th className="px-4 py-3 text-right">Imported</th>
                  <th className="px-4 py-3 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j: import("@/lib/api").ImportJob) => (
                  <tr key={j.id} className="border-t border-border/60">
                    <td className="px-4 py-2">{j.source}</td>
                    <td className="px-4 py-2">
                      <span
                        className={
                          j.status === "completed"
                            ? "text-success"
                            : j.status === "running"
                              ? "text-primary"
                              : j.status === "failed"
                                ? "text-destructive"
                                : "text-muted-foreground"
                        }
                      >
                        {j.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">
                      {new Date(j.startedAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">
                      {j.finishedAt ? new Date(j.finishedAt).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-2 text-right font-mono">
                      {(j.matchedItems ?? 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-right font-mono">
                      {(j.unmatchedItems ?? 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-destructive">
                      {j.failedItems.toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-success">
                      {j.importedItems.toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-right font-mono">
                      {j.totalItems.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <h2 className="mb-3 font-display text-lg font-semibold">
          Recent unmatched / failed products
        </h2>
        {errors.isSuccess && errors.data.data.length === 0 && (
          <p className="text-sm text-muted-foreground">No errors logged.</p>
        )}
        {errors.isSuccess && errors.data.data.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-surface text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">Reason</th>
                </tr>
              </thead>
              <tbody>
                {errors.data.data.slice(0, 50).map(
                  (e: {
                    id: string;
                    createdAt: string;
                    rawPayload?: { TITLE?: string; title?: string };
                    errorMessage?: string;
                  }) => (
                    <tr key={e.id} className="border-t border-border/60">
                      <td className="px-4 py-2 text-muted-foreground">
                        {new Date(e.createdAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-2 text-muted-foreground">
                        {e.rawPayload?.TITLE ?? e.rawPayload?.title ?? "—"}
                      </td>
                      <td className="px-4 py-2 text-destructive">{e.errorMessage}</td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
